import { useState, useEffect } from "react";
import { PokemonSprite } from "@/components/pokemon/pokemon-sprite";
import { StrokedText } from "@/components/ui/text/stroked-text";
import { Direction } from "types/battle-engine/battle";
import { DuelState } from "types/duels/types";
import { useUser } from "@/providers/user-provider";
import { isXsScreen } from "@/utils/screen-size-check";
import { AnimatedVFX } from "@/components/vfx/animated-vfx";

// Add bounce-scale animation CSS
const bounceScaleKeyframes = `
  @keyframes bounce-scale {
    0% { transform: scale(0.95); }
    50% { transform: scale(1.15); }
    100% { transform: scale(1.1); }
  }
`;

export const DuelResult = ({
  duelState,
  onContinue,
}: {
  duelState: DuelState;
  onContinue: () => void;
}) => {
  const { pokeCoins, pokeShards } = useUser();
  const youWon = duelState.result?.winner === "you";
  const isDraw = duelState.result?.winner === "draw";
  const result = duelState.result;

  const coinsGain = result?.coins_gain ?? 0;
  const shardsGain = result?.shards_gain ?? 0;
  const remainingMatches = result?.rewarded_duels_remaining ?? 0;

  const ratingBefore = result?.old_rating ?? 0;
  const ratingAfter = result?.new_rating ?? 0;

  // Animation states
  const [showRating, setShowRating] = useState(false);
  const [showRatingGain, setShowRatingGain] = useState(false);
  const [showRatingUpdate, setShowRatingUpdate] = useState(false);

  const [showRewards, setShowRewards] = useState(false);
  const [showRewardsGain, setShowRewardsGain] = useState(false);
  const [showRewardsUpdate, setShowRewardsUpdate] = useState(false);

  const [showContinue, setShowContinue] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Animated values
  const [animatedRating, setAnimatedRating] = useState(ratingBefore);
  const [animatedCoins, setAnimatedCoins] = useState(pokeCoins);
  const [animatedShards, setAnimatedShards] = useState(pokeShards ?? 0);

  // Animation sequence
  useEffect(() => {
    // 1. Rating
    setTimeout(() => setShowRating(true), 500);
    setTimeout(() => setShowRatingGain(true), 1000);
    setTimeout(() => {
      setShowRatingUpdate(true);
      animateValue(ratingBefore, ratingAfter, setAnimatedRating, 20);
    }, 1500);

    // 2. Rewards
    setTimeout(() => setShowRewards(true), 2000);
    setTimeout(() => setShowRewardsGain(true), 2500);
    setTimeout(() => {
      setShowRewardsUpdate(true);
      animateValue(pokeCoins, pokeCoins + coinsGain, setAnimatedCoins, 30);
      animateValue(
        pokeShards ?? 0,
        (pokeShards ?? 0) + shardsGain,
        setAnimatedShards,
        30,
      );
    }, 3000);

    // 3. Continue
    setTimeout(() => setShowContinue(true), 3500);
  }, []);

  const animateValue = (
    start: number,
    end: number,
    setter: (val: number) => void,
    duration: number,
  ) => {
    const diff = end - start;
    if (diff === 0) {
      setter(end);
      return;
    }
    const steps = Math.abs(diff);
    let currentStep = 0;
    const interval = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;
      const val = Math.round(start + diff * progress);
      setter(val);
      if (currentStep >= steps) {
        clearInterval(interval);
        setter(end);
      }
    }, duration);
  };

  const theme = isDraw
    ? {
        bg: "from-pk-blue-3 to-pk-blue-4",
        lines: "bg-pk-blue-2/40",
        title: "DRAW!",
        text: "text-pk-gray-4",
        border: "border-pk-blue-3",
        cardBg: "bg-pk-blue-3",
        gainStroke: "#60a5fa",
        continueText: "text-pk-blue-1",
        banner: "bg-pk-blue-1",
      }
    : youWon
    ? {
        bg: "from-pk-yellow-3 to-pk-gold-3",
        lines: "bg-pk-gold-1/40",
        title: "VICTORY!",
        text: "text-pk-gray-5",
        border: "border-pk-gold-3",
        cardBg: "bg-pk-gold-3",
        gainStroke: "#77b982",
        continueText: "text-pk-gold-1",
        banner: "bg-pk-gold-1",
      }
    : {
        bg: "from-pk-red-4 to-pk-garnet-1",
        lines: "bg-pk-red-3/50",
        title: "DEFEAT",
        text: "text-pk-gray-4",
        border: "border-pk-red-2",
        cardBg: "bg-pk-red-2",
        gainStroke: "#649a54",
        continueText: "text-pk-red-5",
        banner: "bg-pk-red-1",
      };

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: bounceScaleKeyframes }} />
      <section
        className={`w-full h-full flex flex-col gap-4 xs:gap-6 bg-linear-30 ${theme.bg} relative overflow-hidden py-8 xs:py-12`}
      >
        {/* Title */}
        <div className="w-full flex flex-col items-center justify-center z-10 fill-pk-gray-5 mt-4">
          <StrokedText
            strokeWidth={22}
            fontSize={isXsScreen() ? 36 : 72}
            fillColor={"inherit"}
            strokeColor="#222"
            fontWeight="bold"
          >
            {theme.title}
          </StrokedText>

          {/* Matches Left Badge - Centered below */}
          <div className="flex justify-center relative px-18">
            <div className="absolute top-0 h-full w-3/7 left-1/2 bg-gradient-to-r from-pk-night-2 via-pk-night-2/40 to-transparent z-10" />
            <div className="absolute top-0 h-full w-3/7 left-[10%] bg-gradient-to-r to-pk-night-2 via-pk-night-2/40 from-transparent z-10 " />

            <div className="absolute -top-1.5 h-1 w-3/7 left-1/2 bg-gradient-to-r from-pk-night-2 via-pk-night-2/40 to-transparent z-10" />
            <div className="absolute -top-1.5 h-1 w-3/7 left-[10%] bg-gradient-to-r to-pk-night-2 via-pk-night-2/40 from-transparent z-10 " />

            <div className="absolute -bottom-1.5 h-1 w-3/7 left-1/2 bg-gradient-to-r from-pk-night-2 via-pk-night-2/40 to-transparent z-10" />
            <div className="absolute -bottom-1.5 h-1 w-3/7 left-[10%] bg-gradient-to-r to-pk-night-2 via-pk-night-2/40 from-transparent z-10 " />

            <div
              className={`flex items-center gap-2 px-3 py-1 rounded-full  backdrop-blur-sm relative z-12 `}
            >
              <span className={`text-sm font-bold ${theme.text}`}>
                RANKED DUEL
              </span>
            </div>
          </div>
        </div>

        {/* Pokemon Display */}
        <div className="w-full h-[180px] xs:h-[220px] flex items-end justify-center -space-x-8 xs:-space-x-12 relative z-20 mb-4">
          {duelState.your_team.map((pokemon, index) => {
            if (!pokemon) return null;

            return (
              <div
                key={index}
                className="relative w-[90px] origin-bottom "
                style={{
                  zIndex: index % 2 === 0 ? 3 : 2,
                  transform: `translateY(${
                    index % 2 === 0 ? "0px" : "-75px"
                  }) translateX(${index % 2 === 0 ? "60px" : "16px"})`, // Slight stagger
                }}
              >
                <PokemonSprite
                  pokemon={pokemon}
                  direction={Direction.S}
                  className=""
                  showShadow={true}
                  style={{
                    scale: isXsScreen() ? 3 : 4,
                  }}
                />
              </div>
            );
          })}
        </div>

        {/* Stats Container */}
        <div className="w-full max-w-4xl mx-auto px-4 z-10">
          <div className="grid grid-cols-3 gap-4">
            {/* 1. Rating Card */}
            <div
              className={`flex flex-col gap-2 transition-all duration-500 ${
                showRating
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-4"
              }`}
            >
              <div
                className={`text-center text-sm xs:text-base font-bold ${theme.text} uppercase tracking-wide`}
              >
                Rating
              </div>
              <div
                className={`flex-1 border-4 rounded-xl ${theme.border}  backdrop-blur-sm overflow-hidden flex flex-col items-center justify-center p-4 gap-2 ${theme.cardBg}`}
              >
                <div className="relative">
                  <img
                    src="/assets/icons/medal.png"
                    alt="Rating"
                    className="w-auto h-10 xs:w-auto xs:h-16 drop-shadow-md z-1 relative"
                  />

                  {youWon && !isDraw && (
                    <AnimatedVFX
                      id="medal1"
                      spriteUrl="/assets/spells/vfx/yellow-sparkles__11.png"
                      position={{ x: 45, y: 45 }}
                      style={{ transform: "scale(2.5)" }}
                      loops={99}
                      speed={1}
                      className="w-auto h-10 xs:w-auto xs:h-12 drop-shadow-md absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 !z-10"
                    />
                  )}
                </div>
                <div className="flex flex-col items-center">
                  <StrokedText
                    strokeWidth={8}
                    fontSize={isXsScreen() ? 24 : 32}
                  >
                    {showRatingUpdate
                      ? animatedRating.toString()
                      : ratingBefore.toString()}
                  </StrokedText>

                  {/* Gain/Loss */}
                  <div
                    className={`h-6 flex items-center transition-all duration-300 ${
                      showRatingGain
                        ? "opacity-100 scale-100"
                        : "opacity-0 scale-50"
                    }`}
                  >
                    <StrokedText
                      strokeWidth={6}
                      fontSize={isXsScreen() ? 16 : 20}
                      fillColor="#fff"
                      strokeColor={
                        ratingAfter >= ratingBefore ? "#77b982" : "#9b3938"
                      }
                    >
                      {(ratingAfter - ratingBefore >= 0 ? "+" : "") +
                        (ratingAfter - ratingBefore).toString()}
                    </StrokedText>
                  </div>
                </div>
              </div>
            </div>

            {/* 2. Coins Card */}
            <div
              className={`flex flex-col gap-2 transition-all duration-500 delay-100 ${
                showRewards
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-4"
              }`}
            >
              <div
                className={`text-center text-sm xs:text-base font-bold ${theme.text} uppercase tracking-wide`}
              >
                Coins
              </div>
              <div
                className={`flex-1 border-4 rounded-xl ${theme.border}  backdrop-blur-sm overflow-hidden flex flex-col items-center justify-center p-4 gap-2 ${theme.cardBg}`}
              >
                <div className="relative">
                  <img
                    src="/assets/app-ui/poke-coin.png"
                    alt="Coins"
                    className="w-10 h-10 xs:w-12 xs:h-12 drop-shadow-md z-1 relative"
                  />
                  {youWon && !isDraw && (
                    <AnimatedVFX
                      id="coin1"
                      spriteUrl="/assets/spells/vfx/yellow-sparkles__11.png"
                      position={{ x: 45, y: 45 }}
                      style={{ transform: "scale(2.5)" }}
                      loops={99}
                      speed={1}
                      className="w-auto h-10 xs:w-auto xs:h-12 drop-shadow-md absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 !z-10"
                    />
                  )}
                </div>
                <div className="flex flex-col items-center">
                  <StrokedText
                    strokeWidth={8}
                    fontSize={isXsScreen() ? 24 : 32}
                  >
                    {showRewardsUpdate
                      ? animatedCoins.toString()
                      : pokeCoins.toString()}
                  </StrokedText>

                  {/* Gain */}
                  <div
                    className={`h-6 flex items-center transition-all duration-300 ${
                      showRewardsGain && coinsGain > 0
                        ? "opacity-100 scale-100"
                        : "opacity-0 scale-50"
                    }`}
                  >
                    {coinsGain > 0 && (
                      <StrokedText
                        strokeWidth={6}
                        fontSize={isXsScreen() ? 16 : 20}
                        fillColor="#fff"
                        strokeColor={theme.gainStroke}
                      >
                        +{coinsGain.toString()}
                      </StrokedText>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* 3. Shards Card */}
            <div
              className={`flex flex-col gap-2 transition-all duration-500 delay-200 ${
                showRewards
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-4"
              }`}
            >
              <div
                className={`text-center text-sm xs:text-base font-bold ${theme.text}  tracking-wide`}
              >
                SHARDS
              </div>
              <div
                className={`flex-1 border-4 rounded-xl ${theme.border}   backdrop-blur-sm overflow-hidden flex flex-col items-center justify-center p-4 gap-2 ${theme.cardBg}`}
              >
                <div className="relative">
                  <img
                    src="/assets/app-ui/poke-shard.png"
                    alt="Shards"
                    className="w-auto h-10 xs:w-auto xs:h-12 drop-shadow-md z-1 relative"
                  />
                  {youWon && !isDraw && (
                    <AnimatedVFX
                      id="shard1"
                      spriteUrl="/assets/spells/vfx/yellow-sparkles__11.png"
                      position={{ x: 45, y: 45 }}
                      style={{ transform: "scale(2.5)" }}
                      loops={99}
                      speed={1}
                      className="w-auto h-10 xs:w-auto xs:h-12 drop-shadow-md absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 !z-10"
                    />
                  )}
                </div>
                <div className="flex flex-col items-center">
                  <StrokedText
                    strokeWidth={8}
                    fontSize={isXsScreen() ? 24 : 32}
                  >
                    {showRewardsUpdate
                      ? animatedShards.toString()
                      : (pokeShards ?? 0).toString()}
                  </StrokedText>

                  {/* Gain */}
                  <div
                    className={`h-6 flex items-center transition-all duration-300 ${
                      showRewardsGain && shardsGain > 0
                        ? "opacity-100 scale-100"
                        : "opacity-0 scale-50"
                    }`}
                  >
                    {shardsGain > 0 && (
                      <StrokedText
                        strokeWidth={6}
                        fontSize={isXsScreen() ? 16 : 20}
                        fillColor="#fff"
                        strokeColor={theme.gainStroke}
                      >
                        +{shardsGain.toString()}
                      </StrokedText>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Matches Left Badge - Centered below */}
          <div className="flex justify-center mt-12 relative">
            <div className="absolute top-0 h-full w-3/7 left-1/2 bg-gradient-to-r from-pk-gold-2 via-pk-gold-2/40 to-transparent z-10" />
            <div className="absolute top-0 h-full w-3/7 left-[10%] bg-gradient-to-r to-pk-gold-2 via-pk-gold-2/40 from-transparent z-10 " />

            <div className="absolute -top-1.5 h-1 w-3/7 left-1/2 bg-gradient-to-r from-pk-gold-2 via-pk-gold-2/40 to-transparent z-10" />
            <div className="absolute -top-1.5 h-1 w-3/7 left-[10%] bg-gradient-to-r to-pk-gold-2 via-pk-gold-2/40 from-transparent z-10 " />

            <div className="absolute -bottom-1.5 h-1 w-3/7 left-1/2 bg-gradient-to-r from-pk-gold-2 via-pk-gold-2/40 to-transparent z-10" />
            <div className="absolute -bottom-1.5 h-1 w-3/7 left-[10%] bg-gradient-to-r to-pk-gold-2 via-pk-gold-2/40 from-transparent z-10 " />

            <div
              className={`flex items-center gap-2 px-3 py-1 rounded-full  backdrop-blur-sm relative z-12 `}
            >
              <span className={`text-sm font-bold ${theme.text}`}>
                {remainingMatches}/6 Rewards Left
              </span>
            </div>
          </div>
        </div>

        {/* Continue Button */}
        <div
          className={`w-full flex mt-auto mb-4 items-center justify-center transition-all duration-500 ${
            showContinue ? "opacity-100 scale-100" : "opacity-0 scale-95"
          }`}
        >
          <button
            className={`group transition-all duration-300 ${
              isLoading
                ? "cursor-not-allowed opacity-75"
                : "cursor-pointer active:scale-95 hover:brightness-110"
            }`}
            onClick={() => {
              if (!isLoading) {
                setIsLoading(true);
                onContinue();
              }
            }}
            disabled={isLoading}
          >
            <span
              className={`text-2xl xs:text-4xl font-bold ${
                isLoading ? "text-pk-gray-3" : theme.continueText
              } ${
                !isLoading && "group-hover:text-pk-gray-4"
              } transition-all duration-300 drop-shadow-lg`}
            >
              {isLoading ? "LOADING..." : "CONTINUE"}
            </span>
          </button>
        </div>
      </section>
    </>
  );
};
